import { Injectable } from '@angular/core';
import { CustomerFormOptions, CustomersFormGroup } from '../../shared/interfaces/customer';
import { CustomersFormGroupAdapters } from '../../shared/classes/form-adapter-base';
import { CustomerValidatorAdapter } from '../../shared/classes/form-adapters/customer-validator-adapter';
import { AddressValidatorAdapter } from '../../shared/classes/form-adapters/address-validator-adapter';
import { PreviousEntityAdapter } from '../../shared/classes/form-adapters/previous-entity-adapter';
import { EnhancedFormGroup } from '../../shared/classes/enhanced-formgroup';
import { FormGroup } from '@angular/forms';
import {
    EmploymentHistoryValidatorAdapter,
} from '../../shared/classes/form-adapters/employment-history-validator-adapter';
import { CurrentEntityStatusAdapter } from '../../shared/classes/form-adapters/current-entity-status-adapter';
import { DisableFormControlsAdapter } from '../../shared/classes/form-adapters/disable-form-controls-adapter';
import { AddressTypeAdapter } from '../../shared/classes/form-adapters/address-type-adpater';
import { PaymentType } from '../../shared/enums/payment-type';
import { CustomerFormVariantEnum, CustomerFormVariantType } from '../../shared/interfaces/form';

@Injectable({
    providedIn: 'root',
})
export class CustomersFormAdapterBuilderService {
    public build(
        customersFormGroup: EnhancedFormGroup<CustomersFormGroup>,
        paymentType: PaymentType,
        variant?: CustomerFormVariantType,
        options?: CustomerFormOptions,
    ): CustomersFormGroupAdapters {
        if (!customersFormGroup) {
            return [];
        }

        const strategies: { [key in CustomerFormVariantEnum]?: () => CustomersFormGroupAdapters } = {
            [CustomerFormVariantEnum.SINGLE_BUYER]: () =>
                this.buildSingleBuyer(customersFormGroup, paymentType, options),
            [CustomerFormVariantEnum.CO_BUYER]: () =>
                this.buildCoBuyer(customersFormGroup, paymentType, options),
        };

        return (strategies[variant!] || (() => []))();
    }

    private buildCoBuyer(
        customersFormGroup: EnhancedFormGroup<CustomersFormGroup>,
        paymentType: PaymentType,
        options?: CustomerFormOptions,
    ): CustomersFormGroupAdapters {
        return paymentType === PaymentType.CASH
            ? this.buildCoBuyerCash(customersFormGroup, options)
            : this.buildCoBuyerFinance(customersFormGroup, options);
    }

    private buildCoBuyerCash(
        customersFormGroup: EnhancedFormGroup<CustomersFormGroup>,
        options?: CustomerFormOptions,
    )
        : CustomersFormGroupAdapters {
        return [
            ...this.buildSingleBuyerCash(customersFormGroup, 0, options),
            ...this.buildSingleBuyerCash(customersFormGroup, 1, options),
        ];
    }

    private buildCoBuyerFinance(
        customersFormGroup: EnhancedFormGroup<CustomersFormGroup>,
        options?: CustomerFormOptions,
    )
        : CustomersFormGroupAdapters {
        return [
            ...this.buildSingleBuyerFinance(customersFormGroup, 0, options),
            ...this.buildSingleBuyerFinance(customersFormGroup, 1, options),
        ];
    }

    private buildSingleBuyer(
        customersFormGroup: EnhancedFormGroup<CustomersFormGroup>,
        paymentType: PaymentType,
        options?: CustomerFormOptions,
    ): CustomersFormGroupAdapters {
        return paymentType === PaymentType.CASH
            ? this.buildSingleBuyerCash(customersFormGroup, 0, options)
            : this.buildSingleBuyerFinance(customersFormGroup, 0, options);
    }


    private buildSingleBuyerCash(
        customersFormGroup: EnhancedFormGroup<CustomersFormGroup>,
        customerIndex: number,
        options?: CustomerFormOptions,
    ): CustomersFormGroupAdapters {
        const adapters: CustomersFormGroupAdapters = [];

        // Customer
        adapters.push(new CustomerValidatorAdapter<CustomersFormGroup>(
            customersFormGroup as FormGroup,
            `customers.${customerIndex}`,
            { requireDateOfBirth: false },
        ));

        const alwaysDisabledFieldsList = [
            `customers.${customerIndex}.addresses`,
            `customers.${customerIndex}.employment_histories`,
        ];

        if (options?.disableDateOfBirth) {
            alwaysDisabledFieldsList.push(`customers.${customerIndex}.dateOfBirth`);
        }

        alwaysDisabledFieldsList.forEach((name) => {
            adapters.push(new DisableFormControlsAdapter(
                customersFormGroup as FormGroup, name,
            ));
        });

        return adapters;
    }

    private buildSingleBuyerFinance(
        customersFormGroup: EnhancedFormGroup<CustomersFormGroup>,
        customerIndex: number,
        options?: CustomerFormOptions,
    ): CustomersFormGroupAdapters {

        // All the code here will be moved to sale entry or form settings

        const adapters: CustomersFormGroupAdapters = [];

        // Customer
        adapters.push(new CustomerValidatorAdapter<CustomersFormGroup>(
            customersFormGroup as FormGroup,
            `customers.${customerIndex}`,
        ));

        // Customer Address
        adapters.push(new AddressValidatorAdapter<CustomersFormGroup>(
            customersFormGroup as FormGroup,
            `customers.${customerIndex}.addresses.0`,
        ));
        adapters.push(new AddressValidatorAdapter<CustomersFormGroup>(
            customersFormGroup as FormGroup,
            `customers.${customerIndex}.addresses.1`,
        ));
        adapters.push(new PreviousEntityAdapter<CustomersFormGroup>(
            customersFormGroup as FormGroup,
            `customers.${customerIndex}.addresses`,
        ));
        adapters.push(new CurrentEntityStatusAdapter<CustomersFormGroup>(
            customersFormGroup as FormGroup,
            `customers.${customerIndex}.addresses.0`,
        ));
        adapters.push(new AddressTypeAdapter<CustomersFormGroup>(
            customersFormGroup as FormGroup,
            `customers.${customerIndex}.addresses.0`,
        ));
        adapters.push(new AddressTypeAdapter<CustomersFormGroup>(
            customersFormGroup as FormGroup,
            `customers.${customerIndex}.addresses.1`,
        ));

        // Employment History Adapters
        adapters.push(new EmploymentHistoryValidatorAdapter(
            customersFormGroup as FormGroup,
            `customers.${customerIndex}.employment_histories.0`,
        ));
        adapters.push(new EmploymentHistoryValidatorAdapter(
            customersFormGroup as FormGroup,
            `customers.${customerIndex}.employment_histories.1`,
        ));
        adapters.push(new PreviousEntityAdapter(
            customersFormGroup as FormGroup,
            `customers.${customerIndex}.employment_histories`,
        ));
        adapters.push(new CurrentEntityStatusAdapter<CustomersFormGroup>(
            customersFormGroup as FormGroup,
            `customers.${customerIndex}.employment_histories.0`,
            'isCurrent',
        ));

        // Employment History Address Adapters
        adapters.push(new AddressValidatorAdapter(
            customersFormGroup as FormGroup,
            `customers.${customerIndex}.employment_histories.0.address`,
        ));
        adapters.push(new AddressValidatorAdapter(
            customersFormGroup as FormGroup,
            `customers.${customerIndex}.employment_histories.1.address`,
        ));

        const alwaysDisabledFieldsList = [
            `customers.${customerIndex}.addresses.1.isCurrent`,
            `customers.${customerIndex}.employment_histories.1.isCurrent`,
            `customers.${customerIndex}.employment_histories.0.address.isCurrent`,
            `customers.${customerIndex}.employment_histories.0.address.addressType`,
            `customers.${customerIndex}.employment_histories.0.address.mortgageAmount`,
            `customers.${customerIndex}.employment_histories.0.address.rentalAmount`,
            `customers.${customerIndex}.employment_histories.0.address.startDate`,
            `customers.${customerIndex}.employment_histories.0.address.endDate`,
            `customers.${customerIndex}.employment_histories.1.address.isCurrent`,
            `customers.${customerIndex}.employment_histories.1.address.addressType`,
            `customers.${customerIndex}.employment_histories.1.address.mortgageAmount`,
            `customers.${customerIndex}.employment_histories.1.address.rentalAmount`,
            `customers.${customerIndex}.employment_histories.1.address.startDate`,
            `customers.${customerIndex}.employment_histories.1.address.endDate`,
        ];

        if (options?.disableDateOfBirth) {
            alwaysDisabledFieldsList.push(`customers.${customerIndex}.dateOfBirth`);
        }

        alwaysDisabledFieldsList.forEach((name) => {
            adapters.push(new DisableFormControlsAdapter(
                customersFormGroup as FormGroup, name,
            ));
        });

        return adapters;
    }
}
